import type { FC, ReactNode } from 'react'

import { useRef, useState } from 'react'
import { AnimatePresence, motion, useMotionValueEvent, useScroll } from 'motion/react'
import { cn } from '@/lib/utils'
import { EASE_OUT_EXPO } from '@/components/motion/reveal'
import { RollText } from '@/components/motion/roll-text'
import { ToneSection } from '@/components/generals/tone'

const seeded = (seed: number): number => {
    const value = Math.sin(seed * 12.9898 + 78.233) * 43758.5453

    return value - Math.floor(value)
}

const FRAUD = new Set([37, 82])

const NEW_USERS: Set<number> = (() => {
    const indices = Array.from({ length: 100 }, (_, index) => index)

    for (let index = indices.length - 1; index > 0; index--) {
        const swap = Math.floor(seeded(index + 17) * (index + 1))

        ;[indices[index], indices[swap]] = [indices[swap], indices[index]]
    }

    return new Set(indices.slice(0, 65))
})()

const STEPS = [
    {
        value: '20%',
        title: 'Declined at the door',
        text: 'Existing payment fraud solutions decline as many as 20% of transactions.',
        legend: [{ label: 'Declined transaction', swatch: 'bg-tone' }]
    },
    {
        value: '65%',
        title: 'Mostly new users',
        text: '65% of declined transactions are attributed to new users.',
        legend: [{ label: 'New user', swatch: 'bg-violet' }, { label: 'Returning user', swatch: 'bg-tone' }]
    },
    {
        value: '2%',
        title: 'Actual fraud',
        text: 'Ultimately, a mere 2% of declined transactions are proven to be fraudulent. The other 98 were customers you turned away.',
        legend: [{ label: 'Proven fraud', swatch: 'bg-ember' }, { label: 'Legitimate customer', swatch: 'bg-lavender' }]
    }
]

const squareClass = (index: number, stage: number): string => {
    if (stage === 0) return 'bg-tone'
    if (stage === 1) return NEW_USERS.has(index) ? 'bg-violet' : 'bg-tone'

    return FRAUD.has(index) ? 'bg-ember scale-125 rounded-full' : 'bg-lavender'
}

// One hundred declined transactions, re-read three times as the visitor scrolls.
export const TwoPercent: FC = (): ReactNode => {
    const ref = useRef<HTMLDivElement>(null)
    const [stage, setStage] = useState(0)
    const { scrollYProgress } = useScroll({ target: ref, offset: ['start start', 'end end'] })

    useMotionValueEvent(scrollYProgress, 'change', value => {
        setStage(value < 0.34 ? 0 : value < 0.68 ? 1 : 2)
    })

    const step = STEPS[stage]

    return (
        <ToneSection tone='paper' id='false-declines'>
            <div ref={ref} className='relative h-[320vh]'>
                <div className='sticky top-0 flex h-svh flex-col justify-center overflow-hidden px-5 py-20 md:px-8 lg:px-12'>
                    <div className='grid items-center gap-10 lg:grid-cols-12 lg:gap-16'>
                        <div className='order-2 flex flex-col gap-6 lg:order-1 lg:col-span-5'>
                            <div className='flex items-center justify-between t-label text-tone-muted'>
                                <h2>
                                    (02) The real cost of false declines
                                </h2>
                                <span className='tabular-nums'>
                                    0{stage + 1} / 03
                                </span>
                            </div>

                            <RollText
                                value={step.value}
                                className='font-heading text-[clamp(5rem,15vw,13rem)] font-semibold leading-[0.84] tracking-[-0.05em] wdth-62'
                            />

                            <div className='min-h-[9.5rem] md:min-h-[8rem]'>
                                <AnimatePresence mode='wait'>
                                    <motion.div
                                        key={stage}
                                        initial={{ opacity: 0, y: 18 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        exit={{ opacity: 0, y: -12 }}
                                        transition={{ duration: 0.5, ease: EASE_OUT_EXPO }}
                                        className='flex flex-col gap-3'
                                    >
                                        <p className='font-heading text-2xl font-semibold tracking-tight md:text-3xl'>
                                            {step.title}
                                        </p>
                                        <p className='t-lead max-w-md text-tone-muted'>
                                            {step.text}
                                        </p>
                                    </motion.div>
                                </AnimatePresence>
                            </div>

                            <div className='flex gap-1.5' aria-hidden>
                                {STEPS.map((item, index) => (
                                    <span key={item.value} className='h-0.5 flex-1 overflow-hidden bg-tone-line'>
                                        <span className={cn('block h-full origin-left bg-tone transition-transform duration-700 ease-out-expo', index <= stage ? 'scale-x-100' : 'scale-x-0')} />
                                    </span>
                                ))}
                            </div>
                        </div>

                        <div className='order-1 flex flex-col items-center gap-5 lg:order-2 lg:col-span-7 lg:items-end'>
                            <div
                                role='img'
                                aria-label={`Grid of 100 declined transactions. ${step.text}`}
                                className='grid aspect-square w-[min(78vw,38svh)] grid-cols-10 gap-[0.35rem] md:gap-2 lg:w-[min(42vw,68svh)]'
                            >
                                {Array.from({ length: 100 }, (_, index) => (
                                    <span
                                        key={index}
                                        className={cn('block aspect-square transition-[background-color,transform,border-radius] duration-500 ease-out-expo', squareClass(index, stage))}
                                        style={{ transitionDelay: `${((index % 10) + Math.floor(index / 10)) * 14}ms` }}
                                    />
                                ))}
                            </div>

                            <ul className='flex w-[min(78vw,38svh)] flex-wrap gap-x-6 gap-y-2 t-label text-tone-muted lg:w-[min(42vw,68svh)]'>
                                {step.legend.map(item => (
                                    <li key={item.label} className='flex items-center gap-2'>
                                        <span className={cn('size-2.5', item.swatch)} />
                                        {item.label}
                                    </li>
                                ))}
                            </ul>
                        </div>
                    </div>
                </div>
            </div>
        </ToneSection>
    )
}
